import type { Question, QuestionGroupChild } from "../../util/types";
import type { QuestionCore } from "../contracts";

export type QuestionGroupRaw = Question["question"] & {
  type: "question_group";
  content: string;
  group_id: string;
  active_child_id: string;
  children: QuestionGroupChild[];
};

export type QuestionGroupContainerRaw = Question["question"] & {
  type: "question_group" | "grid_question_group";
  content: string;
  group_id: string;
  active_child_id: string;
  children: QuestionGroupChild[];
};

export type QuestionGroupDefinition = {
  heading: string;
  groupId: string;
  activeChildId: string;
  children: readonly QuestionGroupChild[];
};

export type QuestionGroupDraft = Record<string, never>;
export type QuestionGroupEvent = never;
export type QuestionGroupResponse = { type: "question_group"; value: string };

export const isQuestionGroupRaw = (
  raw: Question["question"],
): raw is QuestionGroupRaw => raw.type === "question_group";

export const isQuestionGroupContainerRaw = (
  raw: Question["question"],
): raw is QuestionGroupContainerRaw =>
  raw.type === "question_group" || raw.type === "grid_question_group";

export const questionGroupContainerValidationError = (
  raw: QuestionGroupContainerRaw,
): string | null => {
  if (typeof raw.group_id !== "string" || !raw.group_id) {
    return "Question group requires a group ID.";
  }
  if (!Array.isArray(raw.children) || raw.children.length === 0) {
    return "Question group requires at least one child.";
  }
  const children = raw.children as unknown[];
  const ids = children.map((child) =>
    child && typeof child === "object" && "id" in child
      ? (child as { id?: unknown }).id
      : undefined,
  );
  if (
    ids.some((id) => typeof id !== "string" || !id) ||
    new Set(ids).size !== ids.length
  ) {
    return "Question group child IDs must be unique strings.";
  }
  if (
    typeof raw.active_child_id !== "string" ||
    !ids.includes(raw.active_child_id)
  ) {
    return "Question group active child is not present.";
  }
  if (
    (children as Partial<QuestionGroupChild>[]).some(
      (candidate) =>
        !Number.isInteger(candidate.field) ||
        typeof candidate.answered !== "boolean" ||
        !candidate.question ||
        typeof candidate.question.type !== "string" ||
        candidate.question.type === "question_group" ||
        candidate.question.type === "grid_question_group",
    )
  ) {
    return "Question group contains an invalid child.";
  }
  const activeChild = (children as QuestionGroupChild[]).find(
    (child) => child.id === raw.active_child_id,
  );
  if (activeChild?.answered) {
    return "Question group active child must be unanswered.";
  }
  return null;
};

export const questionGroupValidationError = (
  raw: Question["question"],
): string | null => {
  if (!isQuestionGroupRaw(raw)) return "Question is not a question group.";
  return questionGroupContainerValidationError(raw);
};

export const hasValidQuestionGroupMetadata = (
  raw: Question["question"],
): raw is QuestionGroupRaw => questionGroupValidationError(raw) === null;

export const questionGroupCore: QuestionCore<
  QuestionGroupRaw,
  QuestionGroupDefinition,
  QuestionGroupDraft,
  QuestionGroupEvent,
  QuestionGroupResponse
> = {
  decode: (raw) => {
    const validationError = questionGroupValidationError(raw);
    if (validationError) throw new Error(validationError);
    return {
      heading: raw.content,
      groupId: raw.group_id,
      activeChildId: raw.active_child_id,
      children: raw.children,
    };
  },
  initialize: () => ({}),
  update: (_definition, draft) => draft,
  validate: () => ({ valid: true }),
  encode: () => {
    throw new Error("Question groups submit through their child modules.");
  },
  projectTranscript: (definition) => ({
    kind: "question_group",
    question: definition.heading,
    answer: "",
  }),
};

export const questionGroupChildRaw = (
  child: QuestionGroupChild,
): Question["question"] => {
  if (!child.answered) return child.question;
  if (child.answer_json !== undefined)
    return { ...child.question, previous_answer_json: child.answer_json };
  if (child.response === undefined) return child.question;
  let previousResponse: QuestionGroupChild["response"] = child.response;
  if (
    child.question.type === "select" &&
    typeof previousResponse === "string" &&
    /^\d+$/.test(previousResponse)
  ) {
    previousResponse = Number(previousResponse);
  }
  return {
    ...child.question,
    previous_response: previousResponse,
  } as Question["question"];
};

export const activeQuestionGroupChild = (
  raw: QuestionGroupContainerRaw,
): QuestionGroupChild | undefined =>
  raw.children.find((child) => child.id === raw.active_child_id);
